/* Deewarjee theme — interactions (vanilla, no dependencies) */
(function () {
  "use strict";

  /* ---------- ANNOUNCEMENT MARQUEE (measured px/second, not guessed seconds) ----------
     Every earlier attempt at a fixed-seconds duration got the actual pixel
     speed wrong by reasoning in the abstract ("36s sounds slow") instead of
     checking the real rendered width — a "12s per repeat" number quietly
     worked out to ~50px/s once real text + padding width was accounted
     for, faster than a value already reported as too fast. Measuring the
     real width and dividing by an explicit, deliberately slow px/second
     target removes that guesswork entirely: whatever the content, it always
     crosses at exactly the speed named below.
     Measured on window "load" (not DOMContentLoaded) — this theme defers
     web-font loading, and DOMContentLoaded can fire before Bowlby/Poppins
     has swapped in, measuring the narrower fallback-font text and locking
     in a too-short duration that then plays back faster once the real
     (wider) font reflows in. "load" fires after fonts/images are done in
     the overwhelming majority of real-world cases, so it measures the
     actual final width. */
  function initMarquee(root) {
    var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return;
    root.querySelectorAll("[data-announce-track]").forEach(function (track) {
      if (track.dataset.marqueeInit) return;
      track.dataset.marqueeInit = "true";
      var speed = parseFloat(track.getAttribute("data-speed")) || 18;
      function measure() {
        var group = track.querySelector(".announce__group");
        if (!group) return;
        var w = group.getBoundingClientRect().width;
        if (w > 0) track.style.setProperty("--marquee-duration", (w / speed) + "s");
      }
      measure();
      if (document.readyState === "complete") {
        measure();
      } else {
        window.addEventListener("load", measure);
      }
      var resizeTimer = null;
      window.addEventListener("resize", function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(measure, 200);
      });
    });
  }

  /* ---------- STICKY HEADER: transparent-over-hero -> white on scroll ---------- */
  function initStickyHeader(root) {
    var header = root.querySelector("[data-sticky-header]");
    if (!header || header.dataset.stickyInit) return;
    header.dataset.stickyInit = "true";

    var announce = document.querySelector("[data-announce]");
    var resizeTimer;
    function measure() {
      var announceH = announce ? announce.getBoundingClientRect().height : 0;
      var navH = header.getBoundingClientRect().height;
      document.documentElement.style.setProperty("--announce-h", announceH + "px");
      if (navH > 0) document.documentElement.style.setProperty("--nav-h", navH + "px");
    }
    measure();
    window.addEventListener("resize", function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(measure, 150);
    });

    var ticking = false;
    function apply() {
      header.classList.toggle("is-scrolled", window.scrollY > 8);
      ticking = false;
    }
    function onScroll() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(apply);
      }
    }
    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  /* ---------- NAV DRAWER (hamburger menu) ---------- */
  function initNavDrawer(root) {
    var burger = root.querySelector("[data-nav-burger]");
    var drawer = root.querySelector("[data-nav-drawer]");
    if (!burger || !drawer || burger.dataset.drawerInit) return;
    burger.dataset.drawerInit = "true";

    function open() {
      drawer.hidden = false;
      requestAnimationFrame(function () { drawer.classList.add("is-open"); });
      burger.setAttribute("aria-expanded", "true");
      document.body.style.overflow = "hidden";
    }
    function close() {
      drawer.classList.remove("is-open");
      burger.setAttribute("aria-expanded", "false");
      document.body.style.overflow = "";
      setTimeout(function () { drawer.hidden = true; }, 300);
    }
    burger.addEventListener("click", open);
    drawer.querySelectorAll("[data-nav-drawer-close]").forEach(function (el) {
      el.addEventListener("click", close);
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && drawer.classList.contains("is-open")) close();
    });
  }

  /* ---------- GENERIC INFINITE-LOOP CAROUSEL ENGINE ----------
     Used by every carousel in the store (hero, best sellers, custom sellers,
     related products, testimonials, PDP gallery). Clone-based seamless loop:
     `visible` real slides are cloned on each side so the wrap point is never
     visually detectable; a continuously-incrementing `current` index is
     reconciled back into [0, realCount) on transitionend so autoplay/swipe
     can never desync from the rendered transform. */
  function createInfiniteCarousel(root, opts) {
    opts = opts || {};
    var track = root.querySelector(opts.trackSelector || "[data-carousel-track]");
    if (!track) return null;
    var realSlides = Array.prototype.slice.call(track.children);
    var realCount = realSlides.length;
    if (realCount < 2) return null;

    var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var getVisible = opts.getVisible || function () { return 1; };
    var dotsContainer = opts.dotsContainer || null;
    var prevBtn = opts.prevBtn || null;
    var nextBtn = opts.nextBtn || null;
    var autoplay = !!opts.autoplay && !reduceMotion;
    var autoplayDelay = opts.autoplayDelay || 4500;
    var onIndexChange = opts.onIndexChange || null;

    var visible = 1, cloneCount = 1, current = 0, isAnimating = false, timer = null, looping = true, animSafety = null;
    var dots = [];

    function clearClones() {
      Array.prototype.slice.call(track.children).forEach(function (n) {
        if (n.hasAttribute("data-clone")) n.remove();
      });
    }

    function makeClone(node) {
      var c = node.cloneNode(true);
      c.setAttribute("data-clone", "true");
      c.setAttribute("aria-hidden", "true");
      c.querySelectorAll("a,button,input,textarea,select,[tabindex]").forEach(function (el) {
        el.setAttribute("tabindex", "-1");
      });
      var self = c.matches("a,button") ? c : null;
      if (self) self.setAttribute("tabindex", "-1");
      return c;
    }

    function step() {
      var first = track.children[cloneCount] || track.children[0];
      if (!first) return 0;
      var cs = getComputedStyle(track);
      var gap = parseFloat(cs.columnGap || cs.gap || "0") || 0;
      return first.getBoundingClientRect().width + gap;
    }

    function offset(idx) { return -((idx + cloneCount) * step()); }

    function wrap(i) { return ((i % realCount) + realCount) % realCount; }

    function buildDots() {
      if (!dotsContainer) return;
      dotsContainer.innerHTML = "";
      dots = [];
      for (var i = 0; i < realCount; i++) {
        var d = document.createElement("button");
        d.type = "button";
        d.className = "hero__dot" + (i === 0 ? " is-active" : "");
        d.setAttribute("aria-label", "Go to slide " + (i + 1));
        (function (idx) { d.addEventListener("click", function () { goTo(idx); }); })(i);
        dotsContainer.appendChild(d);
        dots.push(d);
      }
    }
    function updateDots() {
      dots.forEach(function (d, i) { d.classList.toggle("is-active", i === wrap(current)); });
    }

    function build() {
      var wasReal = realCount ? wrap(current) : 0;
      clearClones();
      visible = Math.max(1, Math.min(realCount - 1, Math.round(getVisible())));
      root.style.setProperty("--carousel-visible", visible);
      looping = realCount > visible;
      if (!looping) {
        track.style.transform = "";
        track.style.transition = "none";
        if (dotsContainer) dotsContainer.innerHTML = "";
        return;
      }
      cloneCount = visible;
      var head = realSlides.slice(-cloneCount).map(makeClone);
      var tail = realSlides.slice(0, cloneCount).map(makeClone);
      head.forEach(function (c) { track.insertBefore(c, track.firstChild); });
      tail.forEach(function (c) { track.appendChild(c); });
      current = wasReal;
      track.style.transition = "none";
      track.style.transform = "translate3d(" + offset(current) + "px,0,0)";
      void track.offsetHeight;
      requestAnimationFrame(function () { track.style.transition = ""; });
      buildDots();
      updateDots();
    }

    function goTo(idx) {
      if (!looping) return;
      if (isAnimating) return;
      current = idx;
      if (reduceMotion) {
        track.style.transition = "none";
        current = wrap(current);
        track.style.transform = "translate3d(" + offset(current) + "px,0,0)";
        updateDots();
        if (onIndexChange) onIndexChange(current);
        return;
      }
      isAnimating = true;
      track.style.transition = "";
      track.style.transform = "translate3d(" + offset(current) + "px,0,0)";
      updateDots();
      if (onIndexChange) onIndexChange(wrap(current));
      /* transitionend can silently fail to fire in plenty of real scenarios
         beyond bfcache — e.g. a lightbox/modal opening mid-transition and
         covering the track, or the tab losing visibility — and every one of
         those would otherwise leave isAnimating stuck true forever, quietly
         killing every future arrow click and swipe. A timeout backstop that
         just matches (with margin) the CSS transition duration means this
         self-heals no matter what interrupts it, without needing to chase
         down every possible interruption individually. */
      clearTimeout(animSafety);
      animSafety = setTimeout(function () { isAnimating = false; }, 500);
    }
    function next() { goTo(current + 1); restart(); }
    function prev() { goTo(current - 1); restart(); }
    function restart() {
      if (!autoplay) return;
      if (timer) clearInterval(timer);
      timer = setInterval(function () { goTo(current + 1); }, autoplayDelay);
    }
    function stop() { if (timer) clearInterval(timer); }

    track.addEventListener("transitionend", function (e) {
      if (e.target !== track || e.propertyName.indexOf("transform") === -1) return;
      clearTimeout(animSafety);
      isAnimating = false;
      if (current >= realCount || current < 0) {
        current = wrap(current);
        track.style.transition = "none";
        track.style.transform = "translate3d(" + offset(current) + "px,0,0)";
        void track.offsetHeight;
        requestAnimationFrame(function () { track.style.transition = ""; });
      }
      /* Re-fire on every settled transition — not just the goTo() call site
         that started it — so drag/swipe/momentum-settle stay in sync with
         anything (like a thumbnail strip) subscribed via onIndexChange. */
      if (onIndexChange) onIndexChange(wrap(current));
    });

    if (nextBtn) nextBtn.addEventListener("click", next);
    if (prevBtn) prevBtn.addEventListener("click", prev);

    /* Pointer drag-to-swipe */
    var dragStartX = 0, dragDX = 0, dragging = false, dragMoved = false, startOffset = 0, dragPointerId = null;
    track.addEventListener("pointerdown", function (e) {
      if (!looping || isAnimating) return;
      /* mouseenter/mouseleave don't fire from a touch drag, so autoplay's
         timer was never actually pausing on mobile — if it fired mid-swipe
         it would yank the track into its own transition on top of the
         user's live drag. Stop it the moment any pointer (touch or mouse)
         engages the track, not just on desktop hover/focus. */
      stop();
      dragging = true;
      dragMoved = false;
      dragStartX = e.clientX;
      startOffset = offset(current);
      track.style.transition = "none";
      dragPointerId = e.pointerId;
      /* setPointerCapture is deliberately NOT called here. Calling it on
         every pointerdown — even a plain click with zero movement —
         retargets the click event that follows to the track element
         instead of whatever card/link the user actually pressed, so the
         click silently never reaches its real target (confirmed: this was
         swallowing every first click on desktop). Capture is only useful
         once we know it's a genuine drag, so it's requested lazily in
         pointermove instead, the first time real movement is confirmed. */
    });
    track.addEventListener("pointermove", function (e) {
      if (!dragging) return;
      dragDX = e.clientX - dragStartX;
      if (Math.abs(dragDX) > 10 && !dragMoved) {
        dragMoved = true;
        track.setPointerCapture && track.setPointerCapture(dragPointerId);
      }
      track.style.transform = "translate3d(" + (startOffset + dragDX) + "px,0,0)";
    });
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      track.style.transition = "";
      var threshold = step() * 0.18;
      if (dragDX < -threshold) { goTo(current + 1); }
      else if (dragDX > threshold) { goTo(current - 1); }
      else { goTo(current); }
      dragDX = 0;
      restart();
    }
    track.addEventListener("pointerup", endDrag);
    track.addEventListener("pointercancel", endDrag);
    track.addEventListener("click", function (e) {
      if (dragMoved) { e.preventDefault(); e.stopPropagation(); dragMoved = false; }
    }, true);

    if (autoplay) {
      root.addEventListener("mouseenter", stop);
      root.addEventListener("mouseleave", restart);
      root.addEventListener("focusin", stop);
      root.addEventListener("focusout", restart);
    }

    var resizeTimer = null;
    window.addEventListener("resize", function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(build, 150);
    });

    /* Mobile back/forward navigation restores the page from bfcache with
       the exact JS state frozen at the moment the user left — if that was
       mid-transition, isAnimating stayed stuck true forever (no future
       transitionend was ever coming to clear it), permanently blocking
       every next/prev click AND swipe from then on. Force a clean, settled
       state whenever the page is restored this way. */
    window.addEventListener("pageshow", function (e) {
      if (!e.persisted) return;
      isAnimating = false;
      dragging = false;
      dragDX = 0;
      if (looping) {
        current = wrap(current);
        track.style.transition = "none";
        track.style.transform = "translate3d(" + offset(current) + "px,0,0)";
        void track.offsetHeight;
        track.style.transition = "";
      }
      restart();
    });

    build();
    restart();

    return { next: next, prev: prev, goTo: goTo, rebuild: build };
  }

  /* ---------- HERO SLIDER (multi-panel, infinite loop) ---------- */
  function initHero(root) {
    function visibleForWidth() {
      /* Clone-safety count, not the literal on-screen slide count — CSS
         drives the actual peek-partial widths (--hero-slide-w). This just
         needs to be >= the number of slides that can be partly visible at
         once (~1.1 mobile / ~2.2 tablet / ~3.3 desktop), rounded up. */
      var w = window.innerWidth;
      if (w < 768) return 2;
      if (w < 1024) return 3;
      return 4;
    }
    createInfiniteCarousel(root, {
      trackSelector: "[data-hero-track]",
      getVisible: visibleForWidth,
      dotsContainer: root.querySelector("[data-hero-dots]"),
      prevBtn: root.querySelector("[data-hero-prev]"),
      nextBtn: root.querySelector("[data-hero-next]"),
      autoplay: true,
      autoplayDelay: 5000
    });
  }

  /* ---------- GENERIC SLIDER (best sellers / custom sellers / related / testimonials) ---------- */
  function initGenericSliders(root) {
    root.querySelectorAll("[data-slider]").forEach(function (slider) {
      if (slider.dataset.carouselInit) return;
      slider.dataset.carouselInit = "true";
      function visibleForWidth() {
        var w = window.innerWidth;
        var itemW = slider.querySelector(".slider__item");
        if (!itemW) return 1;
        var trackW = slider.querySelector("[data-slider-track]").clientWidth;
        var iw = itemW.getBoundingClientRect().width || 1;
        return Math.max(1, Math.round(trackW / iw));
      }
      var autoplayDelay = parseInt(slider.getAttribute("data-slider-autoplay"), 10);
      createInfiniteCarousel(slider, {
        trackSelector: "[data-slider-track]",
        getVisible: visibleForWidth,
        prevBtn: slider.querySelector("[data-slider-prev]"),
        nextBtn: slider.querySelector("[data-slider-next]"),
        autoplay: !!autoplayDelay,
        autoplayDelay: autoplayDelay || undefined
      });
    });
  }

  /* ---------- ACCORDION ---------- */
  function initAccordion(scope) {
    scope.querySelectorAll("[data-acc-h]").forEach(function (h) {
      /* boot() can run more than once against the same DOM (e.g. the theme
         editor firing shopify:section:load repeatedly for the header).
         Without this guard, every extra run stacks another click listener
         on the same button — a second toggle firing in the same click sets
         is-open then immediately flips it back off, so the list appears to
         never open at all. */
      if (h.dataset.accInit) return;
      h.dataset.accInit = "true";
      h.addEventListener("click", function () {
        var item = h.closest(".acc__item");
        if (!item) return;
        var open = item.classList.toggle("is-open");
        var sign = h.querySelector(".acc__sign");
        if (sign) sign.textContent = open ? "–" : "+";
      });
    });
  }

  /* ---------- SHARE BUTTON (native share sheet, clipboard fallback) ---------- */
  function initShare(root) {
    root.querySelectorAll("[data-share-btn]").forEach(function (btn) {
      if (btn.dataset.shareInit) return;
      btn.dataset.shareInit = "true";
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        var url = btn.getAttribute("data-share-url");
        var title = btn.getAttribute("data-share-title") || document.title;
        if (navigator.share) {
          navigator.share({ title: title, url: url }).catch(function () {});
          return;
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(function () {
            btn.classList.add("is-copied");
            setTimeout(function () { btn.classList.remove("is-copied"); }, 1600);
          }).catch(function () {});
        }
      });
    });
  }

  /* ---------- CARD SIZE PICKER + ADD TO CART ---------- */
  function fmtCart(n) { return "Rs." + Math.round(n).toLocaleString("en-US"); }

  function updateCartBadge(count) {
    var cartLink = document.querySelector(".nav__cart");
    if (!cartLink) return;
    var badge = cartLink.querySelector(".nav__badge");
    if (count > 0) {
      if (!badge) {
        badge = document.createElement("span");
        badge.className = "nav__badge";
        cartLink.appendChild(badge);
      }
      badge.textContent = count;
    } else if (badge) {
      badge.remove();
    }
  }

  function initCardClick(root) {
    root.querySelectorAll("[data-card]").forEach(function (card) {
      var url = card.getAttribute("data-card-url");
      if (!url || url === "#") return;
      card.addEventListener("click", function (e) {
        if (e.target.closest("a, button")) return;
        window.location.href = url;
      });
    });
  }

  /* Desktop-only lift-on-hover, driven by JS rather than a CSS :hover
     pseudo-class. WebKit/Chromium mobile browsers can require a first tap to
     "enter" any :hover rule that matches an element or its ancestors before
     a second tap actually fires the click/navigation — even when that rule
     sits behind an (hover:hover) media query, some mobile browsers still
     don't reliably skip the phantom-hover step for it. Gating the listener
     itself behind matchMedia (not just the CSS) means touch devices never
     enter any hover-tracking codepath at all, so there's nothing for that
     quirk to latch onto. */
  function initCardHoverLift(root) {
    if (!window.matchMedia || !window.matchMedia("(hover:hover) and (pointer:fine)").matches) return;
    root.querySelectorAll("[data-card]").forEach(function (card) {
      if (card.dataset.hoverLiftInit) return;
      card.dataset.hoverLiftInit = "true";
      card.addEventListener("mouseenter", function () { card.classList.add("is-card-hovering"); });
      card.addEventListener("mouseleave", function () { card.classList.remove("is-card-hovering"); });
    });
  }

  function initCardImageSwap(root) {
    root.querySelectorAll("[data-card]").forEach(function (card) {
      var media = card.querySelector(".card__media");
      if (!media || !media.querySelector(".card__img--hover")) return;
      function on() { media.classList.add("is-hovering"); }
      function off() { media.classList.remove("is-hovering"); }
      media.addEventListener("pointerenter", on);
      media.addEventListener("pointerleave", off);
      media.addEventListener("touchstart", on, { passive: true });
      media.addEventListener("touchend", off);
      media.addEventListener("touchcancel", off);
    });
  }

  function initCards(root) {
    root.querySelectorAll("[data-card]").forEach(function (card) {
      var sizeGroup = card.querySelector("[data-card-sizes]");
      var priceEl = card.querySelector("[data-card-price]");
      var wasEl = card.querySelector("[data-card-was]");
      var offEl = card.querySelector("[data-card-off]");
      var addBtn = card.querySelector("[data-card-add]");

      if (sizeGroup) {
        sizeGroup.querySelectorAll("[data-card-size]").forEach(function (btn) {
          btn.addEventListener("click", function () {
            if (btn.disabled) return;
            sizeGroup.querySelectorAll("[data-card-size]").forEach(function (b) { b.classList.remove("is-active"); });
            btn.classList.add("is-active");
            var vid = btn.getAttribute("data-variant-id");
            if (addBtn && vid) addBtn.setAttribute("data-variant-id", vid);
            if (priceEl) priceEl.textContent = btn.getAttribute("data-price-html") || priceEl.textContent;
            var compareHtml = btn.getAttribute("data-compare-html");
            if (wasEl) {
              if (compareHtml) { wasEl.textContent = compareHtml; wasEl.hidden = false; }
              else { wasEl.hidden = true; }
            }
            var off = btn.getAttribute("data-off");
            if (offEl) {
              /* visibility, not hidden/display:none — keeps the reserved
                 line height so switching sizes never shifts card height */
              if (off) { offEl.textContent = off + "% OFF"; offEl.style.visibility = ""; }
              else { offEl.style.visibility = "hidden"; }
            }
          });
        });
      }

      if (addBtn) {
        addBtn.addEventListener("click", function (e) {
          e.preventDefault();
          if (addBtn.disabled) return;
          var vid = addBtn.getAttribute("data-variant-id");
          if (!vid) return;
          var originalText = addBtn.textContent;
          addBtn.disabled = true;
          addBtn.textContent = "Adding…";
          var item = { id: vid, quantity: 1 };
          if (addBtn.getAttribute("data-custom-order") === "true") {
            item.properties = { "_Custom Order": "true" };
          }
          fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + "cart/add.js" : "/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ items: [item] }),
          })
            .then(function (res) { if (!res.ok) throw new Error("add failed"); return fetch("/cart.js"); })
            .then(function (res) { return res.json(); })
            .then(function (cart) {
              updateCartBadge(cart.item_count);
              addBtn.textContent = "Added ✓";
              addBtn.classList.add("is-added");
              document.dispatchEvent(new CustomEvent("dwj:added-to-cart", { detail: { variantId: vid } }));
              setTimeout(function () {
                addBtn.textContent = originalText;
                addBtn.classList.remove("is-added");
                addBtn.disabled = false;
              }, 1600);
            })
            .catch(function () {
              addBtn.textContent = "Try again";
              setTimeout(function () { addBtn.textContent = originalText; addBtn.disabled = false; }, 1600);
            });
        });
      }
    });
  }

  /* ---------- GALLERY LIGHTBOX ---------- */
  function initLightbox(scope) {
    var box = scope.querySelector("[data-lightbox]");
    if (!box) return;
    var img = box.querySelector("[data-lightbox-img]");
    var closeBtn = box.querySelector("[data-lightbox-close]");
    var lastFocused = null;
    var openedViaHistory = false;

    function open(src, alt) {
      lastFocused = document.activeElement;
      img.src = src;
      img.alt = alt || "";
      box.hidden = false;
      document.body.style.overflow = "hidden";
      closeBtn.focus();
      /* Push a history entry so Android's back gesture/button has something
         of ours to consume — without this, back skips straight past the
         lightbox and out of the page entirely, since there's nothing telling
         the browser a "screen" was opened. */
      history.pushState({ dwjLightbox: true }, "");
      openedViaHistory = true;
    }
    function reallyClose() {
      box.hidden = true;
      img.src = "";
      document.body.style.overflow = "";
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }
    function close() {
      if (openedViaHistory) {
        openedViaHistory = false;
        /* Go back through the entry open() pushed instead of hiding
           directly — that fires popstate below, which does the actual
           hiding. Keeps the history stack clean so the next real back
           press goes wherever the user actually came from, instead of
           sitting on a leftover lightbox entry. */
        history.back();
      } else {
        reallyClose();
      }
    }
    window.addEventListener("popstate", function () {
      if (!box.hidden) {
        openedViaHistory = false;
        reallyClose();
      }
    });

    scope.querySelectorAll(".gallery__img, .gtile__img").forEach(function (thumb) {
      thumb.style.cursor = "zoom-in";
      thumb.setAttribute("tabindex", "0");
      thumb.setAttribute("role", "button");
      thumb.setAttribute("aria-label", "View larger image");
      function trigger() {
        var full = thumb.getAttribute("data-full") || thumb.currentSrc || thumb.src;
        open(full, thumb.alt);
      }
      thumb.addEventListener("click", trigger);
      thumb.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); trigger(); }
      });
    });

    closeBtn.addEventListener("click", close);
    box.addEventListener("click", function (e) { if (e.target === box) close(); });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && !box.hidden) close();
    });
  }

  /* ---------- QUANTITY STEPPER + PAYMENT TOGGLE + PRICE ---------- */
  function initProduct(scope) {
    /* size options / variants */
    var idInput = scope.querySelector("[name='id']");
    var priceEl = scope.querySelector("[data-price]");
    scope.querySelectorAll("[data-opt-group]").forEach(function (group) {
      var val = scope.querySelector("[data-opt-val]");
      function select(opt) {
        if (opt.getAttribute("aria-disabled") === "true") return;
        group.querySelectorAll(".opt").forEach(function (o) {
          o.classList.remove("is-active");
          o.setAttribute("aria-checked", "false");
          o.setAttribute("tabindex", "-1");
        });
        opt.classList.add("is-active");
        opt.setAttribute("aria-checked", "true");
        opt.setAttribute("tabindex", "0");
        if (val) val.textContent = opt.getAttribute("data-dim") || "";
        var vid = opt.getAttribute("data-variant-id");
        if (vid && idInput) idInput.value = vid;
        var unit = opt.getAttribute("data-unit");
        if (unit) scope.setAttribute("data-unit", unit);
        var priceHtml = opt.getAttribute("data-price-html");
        if (priceHtml && priceEl) priceEl.textContent = priceHtml;
        recalc(scope);
      }
      group.querySelectorAll(".opt").forEach(function (opt) {
        opt.addEventListener("click", function () { select(opt); });
        opt.addEventListener("keydown", function (e) {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            select(opt);
          }
        });
      });
    });

    /* quantity */
    var qn = scope.querySelector("[data-qty]");
    var dec = scope.querySelector("[data-qty-dec]");
    var inc = scope.querySelector("[data-qty-inc]");
    if (dec) dec.addEventListener("click", function () { setQty(getQty() - 1); });
    if (inc) inc.addEventListener("click", function () { setQty(getQty() + 1); });
    function getQty() { return qn ? parseInt(qn.textContent, 10) || 1 : 1; }
    function setQty(v) { v = Math.max(1, v); if (qn) qn.textContent = v; var hid = scope.querySelector("[name='quantity']"); if (hid) hid.value = v; recalc(scope); }

    /* Add to Cart adds via AJAX and stays on the page; Buy It Now adds via
       AJAX then sends the customer to /cart (not straight to checkout) so
       they see the DEEWAR10 / multi-buy promos before paying. Neither ever
       applies a discount itself — the customer types DEEWAR10 themselves
       at checkout if paying by prepaid/bank transfer. */
    var form = scope.querySelector("#product-form");
    var cartBtn = scope.querySelector(".btn-cart");
    var buyBtn = scope.querySelector(".btn-buy");
    if (form && (cartBtn || buyBtn)) {
      form.addEventListener("submit", function (e) {
        e.preventDefault();
        var btn = (e.submitter === buyBtn) ? buyBtn : cartBtn;
        var isBuyNow = btn === buyBtn;
        if (!btn || btn.disabled) return;
        var originalText = btn.textContent;
        btn.disabled = true;
        btn.textContent = isBuyNow ? "Processing…" : "Adding…";
        var body = new FormData(form);
        body.delete("checkout");
        fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + "cart/add.js" : "/cart/add.js", {
          method: "POST",
          headers: { Accept: "application/json" },
          body: body,
        })
          .then(function (res) {
            if (!res.ok) return res.json().then(function (d) { throw new Error(d.description || "add failed"); });
            return res.json();
          })
          .then(function (item) {
            if (isBuyNow) {
              window.location.href = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/") + "cart";
              return;
            }
            return fetch("/cart.js").then(function (r) { return r.json(); }).then(function (cart) {
              updateCartBadge(cart.item_count);
              document.dispatchEvent(new CustomEvent("dwj:added-to-cart", { detail: { variantId: item.variant_id } }));
              btn.textContent = "Added ✓";
              btn.classList.add("is-added");
              setTimeout(function () {
                btn.textContent = originalText;
                btn.classList.remove("is-added");
                btn.disabled = false;
              }, 1600);
            });
          })
          .catch(function () {
            btn.textContent = "Try again";
            setTimeout(function () { btn.textContent = originalText; btn.disabled = false; }, 1600);
          });
      });
    }

    recalc(scope);
  }

  function fmt(n) { return "Rs." + Math.round(n).toLocaleString("en-US"); }

  function recalc(scope) {
    var unit = parseFloat(scope.getAttribute("data-unit") || "0");
    var qn = scope.querySelector("[data-qty]");
    var qty = qn ? parseInt(qn.textContent, 10) || 1 : 1;
    var total = unit * qty;
    var dim = scope.querySelector("[data-opt-val]");
    var totalLabel = scope.querySelector("[data-total-label]");
    var totalAmt = scope.querySelector("[data-total-amt]");
    if (totalLabel) totalLabel.textContent = "Total · " + qty + " × " + (dim ? dim.textContent : "");
    if (totalAmt) totalAmt.textContent = fmt(total);
  }

  /* ---------- COPY TO CLIPBOARD (promo code / IBAN) ---------- */
  function initCopyButtons(root) {
    root.querySelectorAll("[data-copy-btn]").forEach(function (btn) {
      if (btn.dataset.copyInit) return;
      btn.dataset.copyInit = "true";
      btn.addEventListener("click", function () {
        var value = btn.getAttribute("data-copy-value") || "";
        var original = btn.textContent;
        function flash() {
          btn.textContent = "Copied";
          setTimeout(function () { btn.textContent = original; }, 1600);
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(value).then(flash).catch(function () {});
        }
      });
    });
  }

  /* ---------- PDP GALLERY (infinite-loop, swipeable) ---------- */
  function initProductGallery(scope) {
    var root = scope.querySelector("[data-gallery]");
    if (!root) return;
    function visibleForWidth() { return 1; }

    var thumbs = Array.prototype.slice.call(root.querySelectorAll("[data-gallery-thumb]"));
    var thumbsStrip = root.querySelector("[data-gallery-thumbs]");
    function setActiveThumb(idx) {
      thumbs.forEach(function (t, i) { t.classList.toggle("is-active", i === idx); });
      var active = thumbs[idx];
      /* scrollIntoView can walk up to the page itself and yank the whole
         viewport vertically to bring the thumbnail back on screen if the
         visitor has scrolled past the gallery — which, combined with this
         running every 2s on autoplay, is exactly the "page keeps jumping
         back up" bug. Scroll only the thumbnail strip's own scrollLeft so
         the page's vertical scroll position can never be touched. */
      if (active && thumbsStrip) {
        var targetLeft = active.offsetLeft - (thumbsStrip.clientWidth - active.offsetWidth) / 2;
        thumbsStrip.scrollTo({ left: Math.max(0, targetLeft), behavior: "smooth" });
      }
    }

    /* No autoplay here, deliberately: this is the product's OWN image
       gallery, not a marketing carousel — auto-advancing while a shopper
       is examining a specific photo is disorienting, and running a timer
       indefinitely for as long as the PDP is open forces every gallery
       image to eagerly load/decode within seconds regardless of what the
       visitor actually wants to see, which is real, needless CPU/memory/
       battery pressure on the exact page where "Add to Cart" lives. */
    var api = createInfiniteCarousel(root, {
      trackSelector: "[data-gallery-track]",
      getVisible: visibleForWidth,
      dotsContainer: root.querySelector("[data-gallery-dots]"),
      prevBtn: root.querySelector("[data-gallery-prev]"),
      nextBtn: root.querySelector("[data-gallery-next]"),
      autoplay: false,
      onIndexChange: setActiveThumb
    });
    if (!api) return;

    thumbs.forEach(function (t) {
      t.addEventListener("click", function () {
        api.goTo(parseInt(t.getAttribute("data-index"), 10) || 0);
      });
    });

    /* Lightbox: click opens the real (non-dragged) slide's full image */
    root.querySelectorAll("[data-gallery-track] [data-zoom]").forEach(function (img) {
      img.style.cursor = "zoom-in";
    });
  }

  /* ---------- ADD TO CART ATTENTION PULSE ---------- */
  function initAtcPulse(root) {
    var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return;
    var addedKey = "dwj_added_variants";
    var added;
    try { added = JSON.parse(sessionStorage.getItem(addedKey) || "[]"); } catch (e) { added = []; }

    function markAdded(vid) {
      if (!vid || added.indexOf(vid) !== -1) return;
      added.push(vid);
      try { sessionStorage.setItem(addedKey, JSON.stringify(added)); } catch (e) {}
    }
    document.addEventListener("dwj:added-to-cart", function (e) {
      markAdded(e.detail && e.detail.variantId);
      root.querySelectorAll('[data-card-add][data-variant-id="' + (e.detail && e.detail.variantId) + '"]').forEach(function (btn) {
        btn.classList.remove("is-pulsing");
      });
    });

    var io = "IntersectionObserver" in window ? new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var btn = entry.target;
        if (btn.disabled) { btn.classList.remove("is-pulsing"); return; }
        var vid = btn.getAttribute("data-variant-id");
        if (vid && added.indexOf(vid) !== -1) { btn.classList.remove("is-pulsing"); return; }
        btn.classList.toggle("is-pulsing", entry.isIntersecting);
      });
    }, { threshold: 0.4 }) : null;

    root.querySelectorAll("[data-card-add]").forEach(function (btn) {
      if (btn.disabled) return;
      var vid = btn.getAttribute("data-variant-id");
      if (vid && added.indexOf(vid) !== -1) return;
      if (io) io.observe(btn);
      else btn.classList.add("is-pulsing");
    });
  }

  /* ---------- STICKY ADD-TO-CART BAR (mobile PDP) ---------- */
  function initStickyAtcBar(root) {
    var form = root.querySelector("#product-form");
    var mainCart = root.querySelector(".btn-cart");
    var bar = root.querySelector("[data-sticky-atc]");
    if (!form || !mainCart || !bar) return;

    var barCart = bar.querySelector("[data-sticky-atc-cart]");
    if (barCart) {
      barCart.addEventListener("click", function () {
        mainCart.click();
      });
    }

    if (!("IntersectionObserver" in window)) { bar.classList.add("is-visible"); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle("is-visible", !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { threshold: 0 });
    io.observe(mainCart);
  }

  function initReviewVideo(root) {
    root.querySelectorAll("[data-review-video]").forEach(function (player) {
      if (player.dataset.reviewInit) return;
      player.dataset.reviewInit = "true";
      player.addEventListener("click", function () {
        var id = player.getAttribute("data-video-id");
        if (!id) return;
        var iframe = document.createElement("iframe");
        iframe.src = "https://www.youtube.com/embed/" + id + "?autoplay=1&rel=0&playsinline=1";
        iframe.allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
        iframe.allowFullscreen = true;
        iframe.title = "Customer review video";
        player.innerHTML = "";
        player.appendChild(iframe);
      });
    });
  }

  /* ---------- INIT ---------- */
  function boot(root) {
    root = root || document;
    initMarquee(root);
    initReviewVideo(root);
    initStickyHeader(root);
    initNavDrawer(root);
    root.querySelectorAll("[data-hero]").forEach(initHero);
    initAccordion(root);
    root.querySelectorAll("[data-product]").forEach(initProduct);
    root.querySelectorAll("[data-product]").forEach(initLightbox);
    root.querySelectorAll("[data-product]").forEach(initProductGallery);
    initGenericSliders(root);
    initShare(root);
    initCards(root);
    initCardClick(root);
    initCardImageSwap(root);
    initCardHoverLift(root);
    initAtcPulse(root);
    initStickyAtcBar(root);
    initCopyButtons(root);
  }

  document.addEventListener("DOMContentLoaded", function () { boot(document); });

  /* Shopify theme editor: re-init when sections are re-rendered */
  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
})();